import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable } from 'rxjs';
import {
  ConnectionSelfSignup,
  ConnectionSelfSignupStatus,
  ConnectionSelfSignupStatusCounts,
} from '../models/connection-self-signup';
import { PaginatedResponse } from '../../../shared/interfaces/pagination';

@Injectable({
  providedIn: 'root',
})
export class SelfSignupService {
  private apiUrl = environment.apiBaseUrl + '/api';
  private adminApiUrl = environment.apiBaseUrl + '/api/admin';
  private http: HttpClient = inject(HttpClient);

  getAllConnectionSelfSignupApplications(
    pageNo = 1,
    pageSize = 50,
    applicationStatus?: ConnectionSelfSignupStatus,
    search?: string,
    joined?: boolean,
  ): Observable<PaginatedResponse<ConnectionSelfSignup>> {
    let params = new HttpParams();
    params = params.set('page', pageNo.toString());
    params = params.set('page_size', pageSize.toString());
    if (applicationStatus) {
      params = params.set('status', applicationStatus.toString());
    }
    if (joined !== undefined) {
      params = params.set('status', '2'); // Only fetch approved applications when filtering by joined status
      params = params.set('joined', joined ? '1' : '0');
    }
    if (search && search.trim().length > 0) {
      params = params.set('search', search.trim());
    }
    return this.http.get<PaginatedResponse<ConnectionSelfSignup>>(
      `${this.adminApiUrl}/connections/self-signup/applications`,
      { params },
    );
  }

  createConnectionSelfSignupApplication(
    data: ConnectionSelfSignup,
  ): Observable<ConnectionSelfSignup> {
    return this.http.post<ConnectionSelfSignup>(
      `${this.apiUrl}/connections/self-signup`,
      data,
    );
  }

  getConnectionSelfSignupApplicationById(
    applicationId: number,
  ): Observable<ConnectionSelfSignup> {
    return this.http.get<ConnectionSelfSignup>(
      `${this.adminApiUrl}/connections/self-signup/applications/${applicationId}`,
    );
  }

  rejectConnectionSelfSignupApplication(
    applicationId: number,
    approverNotes: string,
  ): Observable<ConnectionSelfSignup> {
    return this.http.put<ConnectionSelfSignup>(
      `${this.adminApiUrl}/connections/self-signup/applications/${applicationId}/status/false`,
      { approver_notes: approverNotes },
    );
  }

  approveConnectionSelfSignupApplication(
    applicationId: number,
    connection_name: string,
    subscriptionPlanName: string | null,
    hasItSupport: boolean,
    approverNotes?: string,
  ): Observable<ConnectionSelfSignup> {
    return this.http.put<ConnectionSelfSignup>(
      `${this.adminApiUrl}/connections/self-signup/applications/${applicationId}/status/true`,
      {
        approver_notes: approverNotes,
        connection_name: connection_name,
        subscription_plan_name: subscriptionPlanName,
        has_it_support: hasItSupport,
      },
    );
  }

  getApplicationStatusCounts(): Observable<ConnectionSelfSignupStatusCounts[]> {
    return this.http.get<ConnectionSelfSignupStatusCounts[]>(
      `${this.adminApiUrl}/connections/self-signup/applications/status/count`,
    );
  }

  validateConnectionSecret(secret: string): Observable<{ valid: boolean }> {
    return this.http.get<{ valid: boolean }>(
      `${this.apiUrl}/auth/auth0/self-signup/connection/validate-secret`,
      { params: { secret } },
    );
  }
}
